import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import getBookingWidgetServerUrl, {
  availableLanguages,
  triplabotSettingAttributes,
  ChatChannels
} from 'javascript/Constants'
import _forEach from 'lodash/forEach'
import { useAppStore } from 'Store/app'
import { matchUrlConditions, isMobile, getIsFirstPartyDomainEnabled } from 'Utilities'

export const useSettingsStore = defineStore('Settings', () => {
  const appStore = useAppStore()

  const settings = ref({
    business_type: null,
    show_upload_file_btn: false,
    allowed_urls: [],
    url_patterns: [],
    limit_url: false,
    mobile_position_bottom: null,
    mobile_position_right: null,
    icon_size_mobile: null,
    pc_position_bottom: null,
    pc_position_right: null,
    icon_size_pc: null,
    terms_and_conditions_enabled: false,
    is_brand_hotel: false,
    cookie_consent_enabled: false,
    minimized_icon: {
      text_color: null,
      color: null
    },
    booking_widget_setting: {
      booking_widget_url: null,
      booking_widget_type: null
    },
    chatbot_channel_settings: [],
    tier: null,
    payment_gateway_setting: {
      name: null,
      shop_id: null,
      token: null,
      script: null
    },
    icon_message_position: null,
    hotel_brand: {
      integrations: []
    }
  })
  const scriptDataAttributes = ref({})
  const isClickLanguageMenu = ref(false)

  // getters
  const isApp = computed(() => settings.value.business_type === 'app')
  const isCreansmaerdActive = computed(() =>
    (settings.value?.hotel_brand?.integrations || []).some(
      (integration) => integration?.integration_type === 'creansmaerd' && integration?.active
    )
  )
  const isOnStandaloneBookingWidget = computed(
    () => scriptDataAttributes.value['data-triplabot-mode'] === 'booking-widget'
  )
  const isCustomBotTestMode = computed(
    () => scriptDataAttributes.value['data-triplabot-mode'] === 'custom-bot-test'
  )
  const isCookieConsentEnabled = computed(
    () => settings.value.cookie_consent_enabled && !isCustomBotTestMode.value
  )
  const isBWOpenInNewTab = computed(
    () => settings.value?.booking_widget_setting?.booking_widget_type === 'standalone'
  )
  const iconMessagePosition = computed(() => settings.value.icon_message_position)
  const chatIconAnimationSettings = computed(() => settings.value.icon_animation || {})
  const bookingWidgetURL = computed(() => {
    const bwURL = settings.value?.booking_widget_setting?.booking_widget_url
    return bwURL || getBookingWidgetServerUrl()
  })
  const paymentGatewaySetting = computed(() => settings.value.payment_gateway_setting)
  const anonymousSignInDataAttributes = computed(() => {
    const attrs = {}
    const attrMap = {
      'data-client-token': 'auth_token',
      'data-auth': 'auth_type'
    }

    _forEach(scriptDataAttributes.value, (value, key) => {
      if (!triplabotSettingAttributes.includes(key)) {
        const attrKey = attrMap[key] || key
        attrs[attrKey] = value
      }
    })

    return attrs
  })

  const triplabotZIndexPosition = computed(() => {
    const zIndex = settings.value?.z_index
    if (zIndex === null || zIndex === undefined) {
      return '2147483647'
    } else {
      return zIndex
    }
  })

  const isBrandFacility = computed(() => settings.value.is_brand_hotel)

  const isChatIconDraggable = computed(
    () => scriptDataAttributes.value['data-triplabot-draggable'] === 'true'
  )

  const triplabotWindowRight = computed(() => {
    const rightPadding = settings.value?.window_right
    if (rightPadding === null || rightPadding === undefined) {
      return '20'
    } else {
      return rightPadding
    }
  })

  const isTermsConditionsEnabled = computed(
    () => settings.value.terms_and_conditions_enabled || false
  )
  const isAIOnlyFacility = computed(() => settings.value.ai_only)
  const isUploadFilesEnabled = computed(() => settings.value.show_upload_file_btn)
  const getSettings = computed(() => settings.value || {})
  const getHotelId = computed(() => settings.value.hotel_id || null)
  const getBrandId = computed(() => settings.value.hotel_brand_id || null)
  const getFacilitySignIn = computed(() => settings.value?.facility_sign_in_enabled || false)
  const getFirstPartyDomainSetting = computed(
    () => settings.value?.hotel_brand?.first_party_cookie_setting
  )
  const isFirstPartyDomainEnable = computed(() => {
    const firstPartyCookieSetting = settings.value?.hotel_brand?.first_party_cookie_setting
    return getIsFirstPartyDomainEnabled(firstPartyCookieSetting?.status)
  })

  const isCurrentLocationWhitelisted = computed(() => {
    if (!appStore.windowLocation) return false

    const currentURL = new URL(appStore.windowLocation)
    if (!settings.value.limit_url || currentURL.origin === 'https://triplabot.tripla.ai')
      return true

    // remove https://www. from URLs
    const fixURL = (url) => url?.replace(/^https?:\/\/(www\.)?(.*)/, '$2')

    return matchUrlConditions(
      settings.value.url_patterns.map((up) => ({ ...up, text: fixURL(up.text) })),
      fixURL(currentURL.toString())
    )
  })
  const availableChatChannels = computed(() => {
    const channels = ['web']
    const isMobileDevice = isMobile()

    settings.value.chatbot_channel_settings.forEach((channel) => {
      if (channel.channel_type === ChatChannels.MESSENGER) {
        if (!isMobileDevice && channel.settings.allow_to_open_from_web) {
          channels.push(channel.channel_type)
        } else if (isMobileDevice && channel.settings.allow_to_open_from_mobile) {
          channels.push(channel.channel_type)
        }
      } else if (
        isMobileDevice &&
        channel.channel_type === ChatChannels.LINE &&
        channel.settings.allow_to_open_from_mobile
      ) {
        channels.push(channel.channel_type)
      } else if (
        channel.channel_type === ChatChannels.WHATSAPP &&
        ((isMobileDevice && channel.settings.allow_to_open_from_mobile) ||
          (!isMobileDevice && channel.settings.allow_to_open_from_desktop))
      ) {
        channels.push(channel.channel_type)
      }
    })

    return channels
  })

  const customLoginURL = computed(() => {
    return settings.value.hotel_brand?.integrations?.find(
      (integ) => integ.integration_type === 'membership_customer_login_redirect' && integ.active
    )?.settings?.url
  })

  const customOrgLoginURL = computed(() => {
    return settings.value.hotel_brand?.integrations?.find(
      (integ) => integ.integration_type === 'membership_customer_login_redirect' && integ.active
    )?.settings?.with_organization_url
  })

  // actions
  function setScriptDataAttributes(value) {
    scriptDataAttributes.value = value
  }

  function updateInitialSettings(value) {
    settings.value = value
  }

  function updateChatHeaders(newSettings) {
    if (Object.keys(settings.value).length === 0 && settings.value.constructor === Object) {
      return
    }

    availableLanguages.forEach((locale) => {
      const newHeaderName = newSettings[locale].chat_header.operator_name
      const newHeaderPosition = newSettings[locale].chat_header.operator_position
      settings.value[locale].chat_header.operator_name = newHeaderName
      settings.value[locale].chat_header.operator_position = newHeaderPosition
    })
  }

  function updateMenuItems(newSettings) {
    if (Object.keys(settings.value).length === 0 && settings.value.constructor === Object) {
      return
    }

    availableLanguages.forEach((locale) => {
      settings.value[locale].menu_items = newSettings[locale].menu_items
    })
  }

  function toggleLanguageMenu(status) {
    isClickLanguageMenu.value = status
  }

  return {
    settings,
    scriptDataAttributes,
    isClickLanguageMenu,
    // getters
    isApp,
    isCookieConsentEnabled,
    isCreansmaerdActive,
    isOnStandaloneBookingWidget,
    isCustomBotTestMode,
    isChatIconDraggable,
    isBWOpenInNewTab,
    iconMessagePosition,
    chatIconAnimationSettings,
    bookingWidgetURL,
    paymentGatewaySetting,
    anonymousSignInDataAttributes,
    triplabotZIndexPosition,
    isBrandFacility,
    triplabotWindowRight,
    isTermsConditionsEnabled,
    isAIOnlyFacility,
    isUploadFilesEnabled,
    getSettings,
    getHotelId,
    getBrandId,
    getFacilitySignIn,
    getFirstPartyDomainSetting,
    isFirstPartyDomainEnable,
    isCurrentLocationWhitelisted,
    availableChatChannels,
    customLoginURL,
    customOrgLoginURL,

    // actions
    setScriptDataAttributes,
    updateInitialSettings,
    updateChatHeaders,
    updateMenuItems,
    toggleLanguageMenu
  }
})
